import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useParams, useNavigate, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import axios from 'axios';
import {
  Search, MapPin, ChevronLeft, ChevronRight, Building2, Heart, Plus,
  LayoutGrid, SlidersHorizontal, X, ImageOff, Globe, CheckCircle2,
  ChevronDown, List, Check
} from 'lucide-react';
import FacebookGroupCTA from '../components/FacebookGroupCTA';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription, SheetFooter, SheetClose } from '../components/ui/sheet';
import useDebouncedSearch from '../hooks/useDebouncedSearch';
import SmartNoResults from '../components/SmartNoResults';
import ListingCard from '../components/ListingCard';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import SignUpPromptModal from '../components/SignUpPromptModal';
import { BUSINESS_CATEGORIES, categoryToSlug, slugToCategory, categoryPath } from '../constants/businessCategories';
import { SeoContentBlock } from '../components/SeoContentBlock';
import CategoryFAQ, { businessFAQs } from '../components/CategoryFAQ';
import { CATEGORY_SEO_DATA } from '../data/categorySEO';
import { ListingGridSkeleton } from '../components/Skeletons';
import SearchAutocomplete from '../components/SearchAutocomplete';
import SaveSearchButton from '../components/SaveSearchButton';
import CategorySpotlight from '../components/CategorySpotlight';

// Legacy category slug → new canonical slug
const LEGACY_CATEGORY_SLUG_REDIRECTS = {
  'cafes-and-restaurants': 'food-and-drink',
  'plumbers-and-heating': 'plumbing-and-heating',
  'accountants-and-financial-services': 'accountancy-and-tax-services',
  'estate-agents-and-property': 'property-services',
  'marketing-and-design': 'marketing-design-and-creative',
  'home-and-garden-services': 'home-and-garden',
  'florists-and-garden': 'florists-and-garden-centres',
  'recruitment-agencies': 'recruitment-and-hr',
  'childcare-and-education': 'education-and-childcare',
  'travel-agents': 'travel-and-tourism',
  'hospitality': 'hospitality-and-accommodation',
  'creative-services': 'marketing-design-and-creative',
  'tradespeople': 'building-and-construction',
};

const API = '/api';
const ITEMS_PER_PAGE = 18;

const BusinessesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { categorySlug } = useParams();
  const navigate = useNavigate();
  const [searchInput, setSearchInput, submitSearch] = useDebouncedSearch(searchParams, setSearchParams);
  const { user } = useAuth();
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [categories, setCategories] = useState([]);
  const [towns, setTowns] = useState([]);
  const [categoryCounts, setCategoryCounts] = useState({});
  const [popularPages, setPopularPages] = useState([]);

  // Watchlist state
  const [watchlist, setWatchlist] = useState(new Set());
  const [watchlistLoading, setWatchlistLoading] = useState(new Set());
  const [showSignUpModal, setShowSignUpModal] = useState(false);

  const currentPage = parseInt(searchParams.get('page') || '1');
  const searchQuery = searchParams.get('q') || '';
  const selectedTown = searchParams.get('town') || '';
  const queryCategory = searchParams.get('category') || '';
  const slugCategory = categorySlug
    ? (slugToCategory(categorySlug) ||
       categories.find(c => categoryToSlug(c) === categorySlug) ||
       '')
    : '';
  const selectedCategory = slugCategory || queryCategory || '';

  // Legacy slug redirect
  useEffect(() => {
    if (categorySlug && LEGACY_CATEGORY_SLUG_REDIRECTS[categorySlug]) {
      const qs = searchParams.toString();
      navigate(
        `/business/${LEGACY_CATEGORY_SLUG_REDIRECTS[categorySlug]}${qs ? `?${qs}` : ''}`,
        { replace: true }
      );
      return;
    }
    if (!categorySlug && queryCategory && categoryToSlug(queryCategory)) {
      const remaining = new URLSearchParams(searchParams);
      remaining.delete('category');
      const qs = remaining.toString();
      navigate(
        `/business/${categoryToSlug(queryCategory)}${qs ? `?${qs}` : ''}`,
        { replace: true }
      );
    } else if (categorySlug && !slugCategory && categories.length > 0) {
      navigate('/businesses', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categorySlug, queryCategory, categories.length, slugCategory]);

  // Dynamic SEO
  let seoTitle = 'Local Businesses in Essex';
  let seoDescription = 'Find trusted local businesses across Essex. Browse verified business listings, read customer reviews, and connect with professional services in Chelmsford, Colchester, Southend-on-Sea and more.';

  if (selectedTown) {
    seoTitle = `Businesses in ${selectedTown}, Essex`;
    seoDescription = `Discover local businesses in ${selectedTown}. Find shops, restaurants, tradespeople, and professional services. Read reviews and contact businesses directly.`;
  }
  if (selectedCategory) {
    seoTitle = selectedCategory + (selectedTown ? ` in ${selectedTown}` : ' in Essex');
    seoDescription = `Find ${selectedCategory.toLowerCase()} businesses${selectedTown ? ` in ${selectedTown}` : ' across Essex'}. Compare services, read reviews, and get quotes from trusted local professionals.`;
  }
  if (searchQuery) {
    seoTitle = `"${searchQuery}" - Business Search Results`;
    seoDescription = `Search results for "${searchQuery}" in Essex businesses. Find local companies and services matching your search.`;
  }
  if (currentPage > 1) {
    seoTitle += ` - Page ${currentPage}`;
  }

  const categorySeo = (categorySlug && !selectedTown && !searchQuery && currentPage === 1)
    ? CATEGORY_SEO_DATA[categorySlug] || null
    : null;
  if (categorySeo) {
    seoTitle = categorySeo.metaTitle;
    seoDescription = categorySeo.metaDescription;
  }
  const categoryCanonicalUrl = categorySlug
    ? `https://discoveressex.co.uk/business/${categorySlug}`
    : 'https://discoveressex.co.uk/businesses';

  const categoryStructuredData = categorySeo ? [
    {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: categorySeo.h1,
      description: categorySeo.metaDescription,
      url: categoryCanonicalUrl,
      isPartOf: { '@type': 'WebSite', name: 'Discover Essex', url: 'https://discoveressex.co.uk' },
      about: { '@type': 'Thing', name: selectedCategory || categorySeo.h1 },
      inLanguage: 'en-GB'
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: (categorySeo.faqs || []).map(({ q, a }) => ({
        '@type': 'Question',
        name: q,
        acceptedAnswer: { '@type': 'Answer', text: a }
      }))
    }
  ] : null;

  // UI state
  const [hideImages, setHideImages] = useState(false);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [seoIntroExpanded, setSeoIntroExpanded] = useState(false);
  // New state for redesign
  const [categorySheetOpen, setCategorySheetOpen] = useState(false);
  const [categorySearch, setCategorySearch] = useState('');
  const [showAllCategories, setShowAllCategories] = useState(false);

  const fetchListings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        listing_type: 'business',
        page: currentPage.toString(),
        limit: ITEMS_PER_PAGE.toString(),
      });
      if (searchQuery) params.append('search', searchQuery);
      if (selectedTown === 'uk-wide') {
        params.append('national_only', 'true');
      } else if (selectedTown) {
        params.append('town', selectedTown);
      }
      if (selectedCategory) params.append('category', selectedCategory);
      const response = await axios.get(`${API}/listings/paginated?${params.toString()}`);
      setListings(response.data.listings || []);
      setTotalCount(response.data.total || 0);
      setTotalPages(response.data.total_pages || 0);
      if (searchQuery) axios.post(`${API}/track-search`, { query: searchQuery, type: 'completed', page: 'business', results_count: response.data.total || 0 }).catch(() => {});
    } catch (error) {
      console.error('Failed to fetch businesses:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, selectedTown, selectedCategory]);

  useEffect(() => { fetchListings(); }, [fetchListings]);

  useEffect(() => {
    const fetchTowns = async () => {
      try {
        const response = await axios.get(`${API}/listings/business-towns`);
        setTowns(response.data.towns || []);
      } catch (error) { console.error('Failed to fetch towns:', error); }
    };
    fetchTowns();
  }, []);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await axios.get(`${API}/category-list/business`);
        setCategories(response.data.categories || BUSINESS_CATEGORIES);
      } catch { setCategories(BUSINESS_CATEGORIES); }
    };
    loadCategories();
  }, []);

  useEffect(() => {
    const fetchCategoryCounts = async () => {
      try {
        const response = await axios.get(`${API}/listings/category-counts/business`);
        setCategoryCounts(response.data.counts || {});
      } catch (error) { console.error('Failed to fetch category counts:', error); }
    };
    fetchCategoryCounts();
  }, []);

  useEffect(() => {
    const fetchPopularPages = async () => {
      try {
        const res = await axios.get(`${API}/seo/popular-pages?limit=12`);
        setPopularPages(res.data.pages || []);
      } catch (err) { console.error('Failed to fetch popular pages:', err); }
    };
    fetchPopularPages();
  }, []);

  useEffect(() => {
    if (user && listings.length > 0) { fetchWatchlistStatus(); }
  }, [user, listings]);

  const fetchWatchlistStatus = async () => {
    try {
      const response = await axios.get(`${API}/watchlist`);
      const watchlistIds = new Set(response.data.map(item => item.listing_id));
      setWatchlist(watchlistIds);
    } catch (error) { console.error('Failed to fetch watchlist:', error); }
  };

  const toggleWatchlist = async (listingId, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) { setShowSignUpModal(true); return; }
    setWatchlistLoading(prev => new Set(prev).add(listingId));
    try {
      if (watchlist.has(listingId)) {
        await axios.delete(`${API}/watchlist/${listingId}`);
        setWatchlist(prev => { const s = new Set(prev); s.delete(listingId); return s; });
        toast.success('Removed from watchlist');
      } else {
        await axios.post(`${API}/watchlist`, { listing_id: listingId });
        setWatchlist(prev => new Set(prev).add(listingId));
        toast.success('Added to watchlist');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update watchlist');
    } finally {
      setWatchlistLoading(prev => { const s = new Set(prev); s.delete(listingId); return s; });
    }
  };

  const updateSearchParams = (key, value) => {
    if (key === 'category') {
      const otherParams = new URLSearchParams(searchParams);
      otherParams.delete('category');
      otherParams.delete('page');
      const qs = otherParams.toString();
      if (value) {
        navigate(`/business/${categoryToSlug(value)}${qs ? `?${qs}` : ''}`);
      } else {
        navigate(`/businesses${qs ? `?${qs}` : ''}`);
      }
      return;
    }
    const newParams = new URLSearchParams(searchParams);
    if (value) { newParams.set(key, value); } else { newParams.delete(key); }
    if (key !== 'page') newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const clearFilters = () => {
    if (categorySlug) { navigate('/businesses'); } else { setSearchParams({}); }
  };

  const handlePageChange = (newPage) => {
    const newParams = new URLSearchParams(searchParams);
    newParams.set('page', newPage.toString());
    setSearchParams(newParams);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const hasActiveFilters = searchQuery || selectedTown || selectedCategory;

  // Sorted categories with counts
  const sortedCategoriesWithCounts = categories
    .filter(cat => categoryCounts[cat] > 0)
    .sort((a, b) => (categoryCounts[b] || 0) - (categoryCounts[a] || 0))
    .map(cat => ({ name: cat, count: categoryCounts[cat] }));

  // Desktop rail computed values
  const topCategories = sortedCategoriesWithCounts.slice(0, 8);
  const railCategories = categorySearch
    ? sortedCategoriesWithCounts.filter(({ name }) => name.toLowerCase().includes(categorySearch.toLowerCase()))
    : (showAllCategories ? sortedCategoriesWithCounts : topCategories);
  // Ensure current category is always visible even if outside top 8
  const showCurrentCategorySeparately = selectedCategory && !railCategories.find(c => c.name === selectedCategory);
  const currentCategoryEntry = showCurrentCategorySeparately
    ? (sortedCategoriesWithCounts.find(c => c.name === selectedCategory) || { name: selectedCategory, count: null })
    : null;

  const getFirstSentence = (text) => {
    if (!text) return '';
    const match = text.match(/^[^.!?]*[.!?]/);
    return match ? match[0] : (text.length > 160 ? text.substring(0, 160) + '\u2026' : text);
  };

  // Shared pagination renderer
  const renderPagination = () => totalPages > 1 ? (
    <div className="mt-6 md:mt-8 flex items-center justify-center gap-2">
      <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage <= 1} className="gap-1" data-testid="prev-page-btn">
        <ChevronLeft className="h-4 w-4" /> Previous
      </Button>
      <div className="flex items-center gap-1">
        {[...Array(Math.min(5, totalPages))].map((_, i) => {
          let pageNum;
          if (totalPages <= 5) pageNum = i + 1;
          else if (currentPage <= 3) pageNum = i + 1;
          else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
          else pageNum = currentPage - 2 + i;
          return (
            <Button key={pageNum} variant={currentPage === pageNum ? 'default' : 'outline'} size="sm"
              onClick={() => handlePageChange(pageNum)}
              className={currentPage === pageNum ? 'bg-[#0F2C3E]' : ''}
              data-testid={`page-${pageNum}-btn`}>
              {pageNum}
            </Button>
          );
        })}
      </div>
      <Button variant="outline" size="sm" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages} className="gap-1" data-testid="next-page-btn">
        Next <ChevronRight className="h-4 w-4" />
      </Button>
    </div>
  ) : null;

  // Watchlist button renderer
  const renderWatchlistBtn = (listingId, size = 'sm') => (
    <button
      onClick={(e) => toggleWatchlist(listingId, e)}
      disabled={watchlistLoading.has(listingId)}
      className={`absolute ${size === 'sm' ? 'top-2 right-2 p-1.5' : 'top-3 right-3 p-2'} z-10 rounded-full transition-all duration-200 ${
        watchlist.has(listingId)
          ? 'bg-red-500 text-white hover:bg-red-600'
          : 'bg-white/90 text-slate-600 hover:bg-white hover:text-red-500 shadow-md'
      } ${watchlistLoading.has(listingId) ? 'opacity-50' : ''}`}
      title={watchlist.has(listingId) ? 'Remove from watchlist' : 'Add to watchlist'}
      data-testid={`watchlist-btn-${listingId}`}
      aria-label={watchlist.has(listingId) ? 'Remove from watchlist' : 'Add to watchlist'}
    >
      <Heart className={`${size === 'sm' ? 'h-4 w-4' : 'h-5 w-5'} ${watchlist.has(listingId) ? 'fill-current' : ''}`} />
    </button>
  );

  // Category advertising CTA map
  const ADV_MAP = {
    "Plumbing & Heating": "plumbers", "Food & Drink": "restaurants",
    "Legal Services": "solicitors", "Building & Construction": "builders",
    "Beauty & Hair": "beauty-salons", "Electricians": "electricians",
    "Cleaning Services": "cleaners", "Automotive & Motoring": "mechanics",
    "Home Improvement & Renovation": "tradesmen", "Accountancy & Tax Services": "accountants",
    "Fitness & Wellbeing": "gyms", "Wedding Services": "wedding-services",
    "Photography & Videography": "photographers", "Painters & Decorators": "painters",
    "Pubs & Bars": "pubs",
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="businesses-page">
      <SEO seoSlug="businesses"
        title={seoTitle}
        description={seoDescription}
        keywords={`local businesses Essex${selectedCategory ? ', ' + selectedCategory + ' Essex' : ''}${selectedTown ? ', businesses ' + selectedTown : ''}, Essex business directory, find businesses Essex`}
        url={categoryCanonicalUrl}
        canonicalUrl={categoryCanonicalUrl}
        structuredData={categoryStructuredData}
        breadcrumbs={[
          { name: 'Home', url: 'https://discoveressex.co.uk' },
          { name: 'Businesses', url: 'https://discoveressex.co.uk/businesses' },
          ...(selectedCategory ? [{ name: selectedCategory, url: `https://discoveressex.co.uk${categoryPath(selectedCategory)}` }] : []),
          ...(selectedTown ? [{ name: selectedTown, url: `https://discoveressex.co.uk/businesses?town=${encodeURIComponent(selectedTown)}` }] : [])
        ]}
      />

      {/* ═══════════════════════════════════════════════════════════════
          MOBILE + TABLET LAYOUT  (< 1024px, lg:hidden)
          ═══════════════════════════════════════════════════════════════ */}
      <div className="lg:hidden">

        {/* Sticky header */}
        <div className="sticky top-0 z-30 bg-white/95 backdrop-blur-md border-b border-slate-200/80 shadow-sm" data-testid="mobile-sticky-header">
          <div className="px-3.5 pt-2.5 pb-0">

            {/* Row 1: H1 + List Business */}
            <div className="flex items-center justify-between gap-2 mb-1.5">
              <h1
                className="text-sm font-bold text-[#0F2C3E] flex items-center gap-1.5 flex-1 min-w-0"
                data-testid="businesses-page-h1-mobile"
              >
                <Building2 className="h-4 w-4 text-blue-600 flex-shrink-0" />
                <span className="truncate">{categorySeo ? categorySeo.h1 : 'Essex Businesses'}</span>
              </h1>
              <Link to={user ? "/create-listing?type=business" : "/signup"} className="flex-shrink-0">
                <Button
                  size="sm"
                  className="bg-[#0F2C3E] hover:bg-[#173f59] text-white text-[11px] h-7 px-2.5 rounded-full whitespace-nowrap"
                  data-testid="create-listing-btn-mobile"
                  onClick={() => axios.post(`${API}/track-click`, { element: 'free_listing_cta', headline: 'Local Businesses', subtext: 'Businesses', cta_text: 'List Business', cta_url: '/create-listing?type=business' }).catch(() => {})}
                >
                  <Plus className="h-3 w-3 mr-0.5" /> List Business
                </Button>
              </Link>
            </div>

            {/* Row 2: Count + About link */}
            <div className="flex items-center gap-2.5 text-[11px] text-slate-500 mb-2">
              {!loading && (
                <span data-testid="mobile-count">
                  <span className="font-semibold text-slate-700">{totalCount}</span> business{totalCount !== 1 ? 'es' : ''}
                </span>
              )}
              <a
                href="#category-description"
                className="text-blue-600 hover:text-blue-800 hover:underline underline-offset-2"
              >
                {categorySeo ? 'About this category' : 'About this directory'}
              </a>
            </div>

            {/* Row 3: Search */}
            <div className="mb-2">
              <SearchAutocomplete
                placeholder="Search businesses..."
                listingType="business"
                onChange={(q) => setSearchInput(q)}
                initialValue={searchInput}
                onSearch={(q) => submitSearch(q)}
                inputClassName="!py-2 !rounded-xl"
              />
            </div>

            {/* Row 4: Category + Town + Filters icon */}
            <div className="flex gap-1.5 items-center pb-2.5">
              {/* Category button */}
              <button
                onClick={() => setCategorySheetOpen(true)}
                className={`flex items-center justify-between gap-1 h-9 px-2.5 rounded-xl border text-[11px] font-medium transition-colors flex-1 min-w-0 ${
                  selectedCategory
                    ? 'bg-[#0F2C3E] text-white border-[#0F2C3E]'
                    : 'bg-slate-50 text-slate-700 border-slate-200 active:bg-slate-100'
                }`}
                data-testid="mobile-category-trigger"
                aria-haspopup="dialog"
              >
                <span className="truncate">{selectedCategory || 'All categories'}</span>
                <ChevronDown className="h-3.5 w-3.5 flex-shrink-0 opacity-60" />
              </button>

              {/* Town select */}
              <Select value={selectedTown || 'all'} onValueChange={(v) => updateSearchParams('town', v === 'all' ? '' : v)}>
                <SelectTrigger className="h-9 text-[11px] rounded-xl border-slate-200 w-[7.5rem] px-2 flex-shrink-0" data-testid="mobile-town-select">
                  <MapPin className="h-3 w-3 text-slate-400 flex-shrink-0 mr-0.5" />
                  <SelectValue placeholder="All locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  <SelectItem value="uk-wide">UK-wide businesses</SelectItem>
                  {towns.length > 0 && (
                    <div className="px-2 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Essex towns</div>
                  )}
                  {towns.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>

              {/* Filters icon */}
              <button
                onClick={() => setFilterDrawerOpen(true)}
                className={`flex-shrink-0 h-9 w-9 rounded-xl border flex items-center justify-center transition-colors ${
                  hideImages
                    ? 'bg-[#0F2C3E] text-white border-[#0F2C3E]'
                    : 'bg-slate-50 text-slate-600 border-slate-200 active:bg-slate-100'
                }`}
                data-testid="mobile-filter-trigger"
                aria-label="Display options"
              >
                <SlidersHorizontal className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>

        {/* Active search chip */}
        {searchQuery && (
          <div className="px-3.5 pt-2 pb-1 flex flex-wrap items-center gap-1.5" data-testid="mobile-active-filters">
            <Badge variant="secondary" className="text-[11px] px-2 py-0.5 bg-blue-50 text-blue-700 border-blue-200">
              &ldquo;{searchQuery}&rdquo;
              <button onClick={() => updateSearchParams('q', '')} className="ml-1" aria-label="Remove search">
                <X className="h-3 w-3" />
              </button>
            </Badge>
            <button onClick={clearFilters} className="text-[11px] text-slate-400 hover:text-red-500 font-medium" data-testid="mobile-clear-all-filters">
              Clear all
            </button>
          </div>
        )}

        {/* Mobile/Tablet content */}
        <div className="px-3.5 pt-3">
          {/* Compact Spotlight */}
          {categorySlug && <CategorySpotlight categorySlug={categorySlug} compact />}

          {/* Results toolbar */}
          {!loading && listings.length > 0 && (
            <div className="flex items-center justify-between mb-3" data-testid="results-toolbar-mobile">
              <p className="text-xs text-slate-500" data-testid="results-count-mobile">
                <span className="font-semibold text-slate-800">{totalCount}</span> business{totalCount !== 1 ? 'es' : ''}
                {totalPages > 1 && <span className="text-slate-400 ml-1">· Page {currentPage}/{totalPages}</span>}
              </p>
            </div>
          )}

          {/* Listing grid: 2-col mobile, 3-col tablet */}
          {loading ? (
            <ListingGridSkeleton count={6} />
          ) : listings.length > 0 ? (
            <div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {listings.map((listing, index) => (
                  <div key={listing.id} className="animate-fade-in-up relative" style={{ animationDelay: `${index * 0.05}s` }}>
                    <ListingCard listing={listing} index={index} hideImages={hideImages} hideCategoryBadge={!!selectedCategory} />
                    {renderWatchlistBtn(listing.id, 'sm')}
                  </div>
                ))}
              </div>
              {renderPagination()}
            </div>
          ) : (
            <SmartNoResults searchQuery={searchQuery} selectedTown={selectedTown} selectedCategory={selectedCategory} onClearFilters={clearFilters} categories={categories} categoryCounts={categoryCounts} />
          )}
          {/* Save Search — shown on all filtered views regardless of auth, below listings */}
          {(selectedCategory || selectedTown || searchQuery) && !loading && (
            <div className="mt-4 mb-2 flex justify-end">
              <SaveSearchButton
                searchType="business"
                category={selectedCategory}
                town={selectedTown}
                keywords={searchQuery}
                hasFilters={!!(selectedCategory || selectedTown || searchQuery)}
              />
            </div>
          )}
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          DESKTOP LAYOUT  (≥ 1024px, hidden lg:block)
          ═══════════════════════════════════════════════════════════════ */}
      <div className="hidden lg:block">

        {/* Desktop header */}
        <div className="bg-white border-b border-slate-200 py-4" data-testid="desktop-header">
          <div className="container mx-auto px-6 lg:px-8">

            {/* Breadcrumb */}
            <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs text-slate-400 mb-2.5">
              <Link to="/" className="hover:text-slate-700 transition-colors">Home</Link>
              <span>/</span>
              <Link to="/businesses" className="hover:text-slate-700 transition-colors">Businesses</Link>
              {selectedCategory && (
                <>
                  <span>/</span>
                  <span className="text-slate-700 font-medium">{selectedCategory}</span>
                </>
              )}
              {selectedTown && !selectedCategory && (
                <>
                  <span>/</span>
                  <span className="text-slate-700 font-medium">{selectedTown}</span>
                </>
              )}
            </nav>

            {/* H1 + List Your Business */}
            <div className="flex items-center justify-between gap-4 mb-2">
              <h1 className="text-2xl lg:text-3xl font-bold text-slate-900" data-testid="businesses-page-h1">
                {categorySeo ? categorySeo.h1 : 'Essex Business Directory'}
              </h1>
              <Link to={user ? "/create-listing?type=business" : "/signup"} className="flex-shrink-0">
                <Button
                  className="bg-blue-600 hover:bg-blue-700 text-white"
                  data-testid="create-listing-btn"
                  onClick={() => axios.post(`${API}/track-click`, { element: 'free_listing_cta', headline: 'Local Businesses', subtext: 'Businesses', cta_text: 'List Your Business', cta_url: '/create-listing?type=business' }).catch(() => {})}
                >
                  <Plus className="h-4 w-4 mr-1.5" /> List Your Business
                </Button>
              </Link>
            </div>

            {/* One-sentence intro + About anchor */}
            <p className="text-slate-600 text-sm mb-3 max-w-2xl">
              {categorySeo
                ? getFirstSentence(categorySeo.intro)
                : 'Discover trusted businesses across Essex \u2014 from restaurants and tradespeople to professional services.'}
              {' '}
              <a
                href="#category-description"
                className="text-blue-600 hover:text-blue-800 font-medium hover:underline underline-offset-2 transition-colors"
              >
                {categorySeo ? 'About this category \u2193' : 'About this directory \u2193'}
              </a>
            </p>

            {/* Combined search bar */}
            <div className="flex gap-2 items-center max-w-2xl mb-2">
              <div className="flex-1 min-w-0">
                <SearchAutocomplete
                  placeholder="Search businesses..."
                  listingType="business"
                  onChange={(q) => setSearchInput(q)} initialValue={searchInput} onSearch={(q) => submitSearch(q)}
                />
              </div>
              <Select value={selectedTown || 'all'} onValueChange={(v) => updateSearchParams('town', v === 'all' ? '' : v)}>
                <SelectTrigger className="h-10 w-44 text-sm" data-testid="desktop-town-select">
                  <MapPin className="h-3.5 w-3.5 mr-1.5 text-slate-400 flex-shrink-0" />
                  <SelectValue placeholder="All locations" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All locations</SelectItem>
                  <SelectItem value="uk-wide">UK-wide businesses</SelectItem>
                  {towns.length > 0 && (
                    <div className="px-2 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Essex towns</div>
                  )}
                  {towns.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button
                onClick={() => submitSearch()}
                className="bg-[#0F2C3E] hover:bg-[#173f59] text-white h-10 px-5 flex-shrink-0"
                data-testid="desktop-search-btn"
              >
                <Search className="h-4 w-4 mr-1.5" /> Search
              </Button>
            </div>

            {/* UK-wide link + active filter badges */}
            <div className="flex flex-wrap items-center justify-between gap-2">
              <Link
                to="/blog/discover-essex-now-welcomes-uk-wide-businesses"
                className="inline-flex items-center gap-1.5 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                data-testid="uk-wide-banner"
              >
                <Globe className="h-3.5 w-3.5 shrink-0" />
                <span>Now open to <span className="font-medium">UK-wide businesses</span> serving Essex</span>
              </Link>
              {searchQuery && (
                <div className="flex flex-wrap gap-1.5">
                  <Badge variant="secondary" className="text-xs px-2 py-0.5">
                    &ldquo;{searchQuery}&rdquo;
                    <button onClick={() => updateSearchParams('q', '')} className="ml-1" aria-label="Remove search"><X className="h-3 w-3" /></button>
                  </Badge>
                  <button onClick={clearFilters} className="text-xs text-slate-400 hover:text-red-500" data-testid="clear-filters-inline">
                    <X className="h-3.5 w-3.5 inline mr-0.5" /> Clear filters
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Two-column layout */}
        <div className="container mx-auto px-6 lg:px-8 py-6">
          <div className="flex gap-6 xl:gap-8 items-start">

            {/* ── LEFT RAIL ── */}
            <aside className="w-64 flex-shrink-0 sticky top-20 self-start" aria-label="Category navigation and filters">
              <div className="bg-white rounded-xl border border-slate-200 max-h-[calc(100vh-5.5rem)] overflow-y-auto">

                {/* Heading */}
                <div className="px-4 pt-4 pb-2">
                  <h2 className="text-sm font-semibold text-slate-900">Categories</h2>
                </div>

                {/* Find a category */}
                <div className="px-3 pb-2">
                  <div className="relative">
                    <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
                    <input
                      type="text"
                      placeholder="Find a category..."
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      className="w-full pl-8 pr-3 py-2 text-xs rounded-lg border border-slate-200 bg-slate-50 focus:outline-none focus:ring-2 focus:ring-[#0F2C3E]/20 focus:border-[#0F2C3E] transition-colors"
                      data-testid="category-rail-search"
                    />
                  </div>
                </div>

                {/* All businesses */}
                <div className="px-3 pb-1">
                  <Link
                    to="/businesses"
                    className={`flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors min-h-[36px] ${
                      !selectedCategory ? 'bg-[#0F2C3E] text-white' : 'text-slate-700 hover:bg-slate-100'
                    }`}
                    data-testid="category-rail-all"
                  >
                    <span>All businesses</span>
                  </Link>
                </div>

                {/* Category list */}
                <div className="px-3 pb-2">
                  {/* Current category if outside visible range */}
                  {currentCategoryEntry && (
                    <Link
                      to={`/business/${categoryToSlug(currentCategoryEntry.name)}`}
                      className="flex items-center justify-between px-3 py-1.5 rounded-lg text-sm bg-[#0F2C3E] text-white font-medium mb-1 min-h-[36px]"
                      data-testid={`category-rail-current-${categoryToSlug(currentCategoryEntry.name)}`}
                    >
                      <span className="truncate mr-2">{currentCategoryEntry.name}</span>
                      {currentCategoryEntry.count !== null && (
                        <span className="text-white/60 text-xs flex-shrink-0">{currentCategoryEntry.count}</span>
                      )}
                    </Link>
                  )}

                  {railCategories.map(({ name, count }) => (
                    <Link
                      key={name}
                      to={`/business/${categoryToSlug(name)}`}
                      className={`flex items-center justify-between px-3 py-1.5 rounded-lg text-sm transition-colors min-h-[36px] ${
                        selectedCategory === name
                          ? 'bg-[#0F2C3E] text-white font-medium'
                          : 'text-slate-700 hover:bg-slate-100'
                      }`}
                      data-testid={`category-rail-${categoryToSlug(name)}`}
                    >
                      <span className="truncate mr-2">{name}</span>
                      <span className={`text-xs flex-shrink-0 ${selectedCategory === name ? 'text-white/60' : 'text-slate-400'}`}>{count}</span>
                    </Link>
                  ))}
                </div>

                {/* Show all / Show less */}
                {!categorySearch && sortedCategoriesWithCounts.length > 8 && (
                  <div className="px-3 pb-3">
                    <button
                      onClick={() => setShowAllCategories(v => !v)}
                      className="w-full text-xs text-blue-700 hover:text-blue-900 font-medium py-1.5 rounded-lg hover:bg-blue-50 transition-colors"
                      data-testid="show-all-categories-btn"
                    >
                      {showAllCategories
                        ? '\u2191 Show less'
                        : `Show all ${sortedCategoriesWithCounts.length} categories \u2193`}
                    </button>
                  </div>
                )}

                {/* Show only filters */}
                <div className="border-t border-slate-100 px-4 py-3 space-y-2.5">
                  <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Show only</p>

                  {/* Town */}
                  <Select value={selectedTown || 'all'} onValueChange={(v) => updateSearchParams('town', v === 'all' ? '' : v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid="rail-town-filter">
                      <MapPin className="h-3 w-3 mr-1 text-slate-400 flex-shrink-0" />
                      <SelectValue placeholder="All locations" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All locations</SelectItem>
                      <SelectItem value="uk-wide">UK-wide businesses</SelectItem>
                      {towns.length > 0 && (
                        <div className="px-2 py-1 text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Essex towns</div>
                      )}
                      {towns.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                    </SelectContent>
                  </Select>

                  {/* Compact view toggle */}
                  <button
                    type="button"
                    onClick={() => setHideImages(!hideImages)}
                    className={`flex items-center gap-2 w-full px-3 py-2 rounded-lg border text-xs font-medium transition-all min-h-[32px] ${
                      hideImages
                        ? 'bg-slate-800 text-white border-slate-800'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid="rail-compact-toggle"
                  >
                    {hideImages ? <LayoutGrid className="h-3.5 w-3.5" /> : <ImageOff className="h-3.5 w-3.5" />}
                    {hideImages ? 'Grid view' : 'Compact view'}
                    {hideImages && <CheckCircle2 className="h-3.5 w-3.5 ml-auto" />}
                  </button>

                  {/* Clear filters */}
                  {hasActiveFilters && (
                    <button
                      onClick={clearFilters}
                      className="text-xs text-red-500 hover:text-red-700 font-medium flex items-center gap-1"
                      data-testid="rail-clear-filters"
                    >
                      <X className="h-3 w-3" /> Clear all filters
                    </button>
                  )}
                </div>
              </div>
            </aside>

            {/* ── MAIN RESULTS COLUMN ── */}
            <main className="flex-1 min-w-0">
              {/* Compact Spotlight */}
              {categorySlug && <CategorySpotlight categorySlug={categorySlug} compact />}

              {/* Toolbar */}
              {!loading && (
                <div className="flex items-center justify-between mb-4 pb-3 border-b border-slate-200" data-testid="results-toolbar">
                  <p className="text-sm text-slate-600" data-testid="results-count">
                    <span className="font-semibold text-slate-900">{totalCount}</span> business{totalCount !== 1 ? 'es' : ''}
                    {totalPages > 1 && <span className="text-slate-400 ml-1">· Page {currentPage}/{totalPages}</span>}
                  </p>
                  <button
                    type="button"
                    onClick={() => setHideImages(!hideImages)}
                    title={hideImages ? 'Grid view (show images)' : 'Compact view (hide images)'}
                    className={`p-2 rounded-lg border transition-colors ${
                      hideImages
                        ? 'bg-slate-800 text-white border-slate-800'
                        : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}
                    data-testid="results-view-toggle"
                    aria-label={hideImages ? 'Switch to grid view' : 'Switch to compact view'}
                  >
                    {hideImages ? <LayoutGrid className="h-4 w-4" /> : <List className="h-4 w-4" />}
                  </button>
                </div>
              )}

              {/* 3-col listing grid */}
              {loading ? (
                <ListingGridSkeleton count={9} />
              ) : listings.length > 0 ? (
                <div>
                  <div className="grid grid-cols-3 gap-4">
                    {listings.map((listing, index) => (
                      <div key={listing.id} className="animate-fade-in-up relative" style={{ animationDelay: `${index * 0.05}s` }}>
                        <ListingCard listing={listing} index={index} hideImages={hideImages} hideCategoryBadge={!!selectedCategory} />
                        {renderWatchlistBtn(listing.id, 'md')}
                      </div>
                    ))}
                  </div>
                  {renderPagination()}
                  {(selectedCategory || selectedTown || searchQuery) && (
                    <div className="mt-4 flex justify-end">
                      <SaveSearchButton searchType="business" category={selectedCategory} town={selectedTown} keywords={searchQuery} hasFilters={!!(selectedCategory || selectedTown || searchQuery)} />
                    </div>
                  )}
                </div>
              ) : (
                <SmartNoResults searchQuery={searchQuery} selectedTown={selectedTown} selectedCategory={selectedCategory} onClearFilters={clearFilters} categories={categories} categoryCounts={categoryCounts} />
              )}
            </main>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          SHARED BELOW-FOLD CONTENT  (all viewports)
          id="category-description" — target for "About this category" anchors
          ═══════════════════════════════════════════════════════════════ */}
      <div id="category-description" className="container mx-auto px-3.5 md:px-6 lg:px-8 py-8 md:py-10 scroll-mt-20">

        {/* Long-form SEO content */}
        {categorySeo ? (
          <>
            {/* Collapsible intro */}
            <section
              className="bg-white rounded-2xl border border-slate-200 p-5 md:p-8 mb-6"
              data-testid="category-seo-intro"
              aria-labelledby="cat-seo-h2"
            >
              <div
                id="cat-seo-content"
                className={`relative overflow-hidden transition-[max-height] duration-300 ease-out ${
                  seoIntroExpanded ? 'max-h-[2000px]' : 'max-h-32 md:max-h-40'
                }`}
              >
                <p className="text-slate-700 text-sm md:text-[17px] leading-relaxed">{categorySeo.intro}</p>
                {categorySeo.whatToLookFor && (
                  <div className="mt-4 md:mt-6">
                    <h2 id="cat-seo-h2" className="text-base md:text-xl font-semibold text-slate-900 mb-2">
                      {categorySeo.whatToLookFor.title}
                    </h2>
                    <p className="text-slate-700 text-sm md:text-base leading-relaxed">{categorySeo.whatToLookFor.body}</p>
                  </div>
                )}
                {!seoIntroExpanded && (
                  <div className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white to-transparent" aria-hidden="true" />
                )}
              </div>
              <button
                type="button"
                onClick={() => setSeoIntroExpanded(v => !v)}
                aria-expanded={seoIntroExpanded}
                aria-controls="cat-seo-content"
                className="mt-3 text-sm font-semibold text-blue-700 hover:text-blue-800 underline-offset-2 hover:underline"
                data-testid="category-seo-intro-toggle"
              >
                {seoIntroExpanded ? 'Show less' : 'Read more'}
              </button>
            </section>

            {categorySeo.whyLocal && (
              <section className="bg-blue-50 border border-blue-100 rounded-2xl p-5 md:p-8 mb-6" data-testid="category-seo-why-local" aria-labelledby="cat-seo-why-h2">
                <h2 id="cat-seo-why-h2" className="text-base md:text-xl font-semibold text-slate-900 mb-2">{categorySeo.whyLocal.title}</h2>
                <p className="text-slate-700 text-sm md:text-base leading-relaxed">{categorySeo.whyLocal.body}</p>
              </section>
            )}

            {Array.isArray(categorySeo.relatedCategories) && categorySeo.relatedCategories.length > 0 && (
              <section className="mb-6" data-testid="category-seo-related" aria-labelledby="cat-seo-related-h2">
                <h2 id="cat-seo-related-h2" className="text-base md:text-lg font-semibold text-slate-900 mb-3">Related Essex categories</h2>
                <div className="flex flex-wrap gap-2">
                  {categorySeo.relatedCategories.map((rc) => (
                    <Link key={rc.slug} to={`/business/${rc.slug}`} className="inline-flex items-center px-3 py-1.5 rounded-full text-sm bg-white border border-slate-200 text-slate-700 hover:border-blue-400 hover:text-blue-700 transition-colors" data-testid={`related-category-${rc.slug}`}>{rc.label}</Link>
                  ))}
                </div>
              </section>
            )}

            {Array.isArray(categorySeo.faqs) && categorySeo.faqs.length > 0 && (
              <section className="mb-6" data-testid="category-seo-faqs" aria-labelledby="cat-seo-faqs-h2">
                <h2 id="cat-seo-faqs-h2" className="text-lg md:text-xl font-semibold text-slate-900 mb-4">Frequently asked questions</h2>
                <div className="space-y-3">
                  {categorySeo.faqs.map((f, idx) => (
                    <div key={idx} className="bg-white border border-slate-200 rounded-xl p-4 md:p-5" data-testid={`category-seo-faq-${idx}`}>
                      <h3 className="text-base font-semibold text-slate-900 mb-1.5">{f.q}</h3>
                      <p className="text-slate-700 text-sm md:text-base leading-relaxed">{f.a}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}
          </>
        ) : (
          <>
            <SeoContentBlock collapsible>
              <h2>Discover Local Businesses Across Essex</h2>
              <p>
                Essex is home to a thriving community of independent businesses, skilled tradespeople, and professional service providers. Whether you are searching for a reliable plumber, a talented graphic designer, or a trusted accountant, Discover Essex connects you with verified local businesses operating throughout the county. Our directory is designed to make it straightforward to find, compare, and contact businesses that serve your area.
              </p>
              <p>
                From the bustling high streets of <Link to="/town/chelmsford">Chelmsford</Link> to the historic market squares of <Link to="/town/colchester">Colchester</Link>, Essex businesses offer everything from home improvement and landscaping to IT support and financial advice. Many of these businesses have been serving their communities for decades, building reputations based on quality workmanship and excellent customer service. By browsing our listings, you can read genuine reviews from other Essex residents who have used these services first-hand.
              </p>
              <h3>Why Choose a Local Essex Business?</h3>
              <p>
                Supporting local businesses strengthens the Essex economy and keeps money circulating within the community. Local providers often deliver a more personal, responsive service compared to national chains. They understand the specific needs of Essex residents, whether that involves navigating local planning regulations, sourcing materials from nearby suppliers, or simply being available at short notice. When you hire a local business through Discover Essex, you are investing directly in the prosperity of towns like <Link to="/town/basildon">Basildon</Link> and the surrounding areas.
              </p>
              <h3>Browse by Category or Location</h3>
              <p>
                Our business directory covers a wide range of categories including construction, cleaning, gardening, health and beauty, automotive, and professional services. You can filter results by town, category, or use the search bar to find exactly what you need. If you cannot find a service you are looking for, consider posting a request on our <Link to="/find-a-trade">Find a Trade</Link> page, where local businesses can come to you with quotes and proposals.
              </p>
              <p>
                Whether you are a homeowner in need of a kitchen renovation, a landlord seeking a dependable property manager, or a start-up looking for office space, our Essex business listings provide a comprehensive resource. Explore the full directory, read reviews from your neighbours, and connect with trusted professionals in your area today. You can also browse <Link to="/jobs">job opportunities</Link> posted by these businesses or discover <Link to="/events">local events</Link> happening across the county.
              </p>
              <h3>Find Services by Town</h3>
              <p>
                Looking for a specific service in your area? Browse our dedicated pages for popular services across Essex:
              </p>
              {popularPages.length > 0 && (
                <div className="not-prose flex flex-wrap gap-1.5 mt-2 mb-3">
                  {popularPages.map((page) => (
                    <Link key={page.url} to={page.url} className="no-underline">
                      <span className="inline-block px-2.5 py-1 rounded-full text-xs bg-slate-100 text-slate-600 hover:bg-slate-200 hover:text-[#0F2C3E] transition-colors cursor-pointer">{page.label} ({page.count})</span>
                    </Link>
                  ))}
                </div>
              )}
            </SeoContentBlock>
            <CategoryFAQ faqs={businessFAQs} category="businesses" />
          </>
        )}

        {/* ── Browse all categories — alphabetical multi-column ── */}
        {sortedCategoriesWithCounts.length > 0 && (
          <section className="mt-8 mb-8" data-testid="browse-all-categories-section" aria-labelledby="browse-all-h2">
            <h2 id="browse-all-h2" className="text-base md:text-lg font-semibold text-slate-900 mb-4">Browse all categories</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-0">
              {[...sortedCategoriesWithCounts]
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(({ name, count }) => (
                  <Link
                    key={name}
                    to={`/business/${categoryToSlug(name)}`}
                    className="flex items-center justify-between py-2 border-b border-slate-100 text-sm text-slate-700 hover:text-blue-700 transition-colors group"
                    data-testid={`browse-all-${categoryToSlug(name)}`}
                  >
                    <span className="group-hover:underline underline-offset-2 truncate mr-2">{name}</span>
                    <span className="text-slate-400 text-xs flex-shrink-0">{count}</span>
                  </Link>
                ))}
            </div>
            {/* Mobile-only: open category sheet */}
            <button
              className="lg:hidden w-full mt-4 py-3 text-sm text-[#0F2C3E] font-semibold border border-slate-200 rounded-xl bg-white hover:bg-slate-50 transition-colors min-h-[44px]"
              onClick={() => setCategorySheetOpen(true)}
              data-testid="browse-all-categories-btn"
            >
              Browse all {sortedCategoriesWithCounts.length} categories \u2192
            </button>
          </section>
        )}

        <FacebookGroupCTA page="Businesses" />

        {/* Category advertising cross-link */}
        {selectedCategory && (() => {
          const advSlug = ADV_MAP[selectedCategory] || "essex-businesses";
          return (
            <div className="mt-6 bg-gradient-to-r from-[#0F2C3E] to-[#1a4a62] rounded-xl p-5 md:p-6 text-white">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                <div>
                  <p className="font-semibold text-sm md:text-base">Own a {selectedCategory.toLowerCase()} business in Essex?</p>
                  <p className="text-slate-300 text-xs md:text-sm mt-0.5">Get premium placement and reach more local customers</p>
                </div>
                <Link to={`/advertise/${advSlug}`}>
                  <Button size="sm" className="bg-[#D4A017] hover:bg-[#c49516] text-[#0F2C3E] font-semibold whitespace-nowrap" data-testid="category-advertise-cta">
                    See advertising options
                  </Button>
                </Link>
              </div>
            </div>
          );
        })()}
      </div>

      {/* ═══════════════════════════════════════════════════════════════
          MOBILE CATEGORY SHEET
          ═══════════════════════════════════════════════════════════════ */}
      <Sheet
        open={categorySheetOpen}
        onOpenChange={(open) => { setCategorySheetOpen(open); if (!open) setCategorySearch(''); }}
      >
        <SheetContent side="bottom" className="max-h-[90vh] rounded-t-2xl flex flex-col p-0" data-testid="category-bottom-sheet">
          <SheetHeader className="text-left px-4 pt-4 pb-3 border-b border-slate-100 flex-shrink-0">
            <SheetTitle className="text-lg font-bold text-slate-900">Categories</SheetTitle>
          </SheetHeader>

          {/* Search */}
          <div className="px-4 py-3 flex-shrink-0 border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <input
                type="text"
                placeholder="Search categories..."
                value={categorySearch}
                onChange={(e) => setCategorySearch(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#0F2C3E]/20 focus:border-[#0F2C3E]"
                data-testid="category-sheet-search"
              />
            </div>
          </div>

          {/* Scrollable list */}
          <div className="overflow-y-auto flex-1">
            {/* All businesses */}
            <Link
              to="/businesses"
              onClick={() => { setCategorySheetOpen(false); setCategorySearch(''); }}
              className={`flex items-center justify-between px-4 py-3.5 text-sm font-medium border-b border-slate-100 min-h-[44px] ${
                !selectedCategory ? 'text-[#0F2C3E] bg-slate-50' : 'text-slate-700 hover:bg-slate-50'
              }`}
              data-testid="category-sheet-all"
            >
              <span>All businesses</span>
              {!selectedCategory && <Check className="h-4 w-4 text-[#0F2C3E] flex-shrink-0" />}
            </Link>

            {/* Most listings top 5 (when no search) */}
            {!categorySearch && topCategories.slice(0, 5).length > 0 && (
              <>
                <div className="px-4 py-2 bg-slate-50 border-b border-slate-100">
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">Most listings</span>
                </div>
                {topCategories.slice(0, 5).map(({ name, count }) => (
                  <Link
                    key={`popular-${name}`}
                    to={`/business/${categoryToSlug(name)}`}
                    onClick={() => { setCategorySheetOpen(false); setCategorySearch(''); }}
                    className={`flex items-center justify-between px-4 py-3.5 text-sm border-b border-slate-100 min-h-[44px] ${
                      selectedCategory === name ? 'bg-slate-50 font-semibold text-[#0F2C3E]' : 'text-slate-700 hover:bg-slate-50'
                    }`}
                    data-testid={`category-sheet-popular-${categoryToSlug(name)}`}
                  >
                    <span>{name}</span>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-slate-400">{count}</span>
                      {selectedCategory === name && <Check className="h-4 w-4 text-[#0F2C3E]" />}
                    </div>
                  </Link>
                ))}
              </>
            )}

            {/* All A–Z */}
            {!categorySearch && (
              <div className="px-4 py-2 bg-slate-50 border-b border-slate-100">
                <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">All categories A–Z</span>
              </div>
            )}
            {(categorySearch
              ? sortedCategoriesWithCounts.filter(({ name }) => name.toLowerCase().includes(categorySearch.toLowerCase()))
              : [...sortedCategoriesWithCounts].sort((a, b) => a.name.localeCompare(b.name))
            ).map(({ name, count }) => (
              <Link
                key={`az-${name}`}
                to={`/business/${categoryToSlug(name)}`}
                onClick={() => { setCategorySheetOpen(false); setCategorySearch(''); }}
                className={`flex items-center justify-between px-4 py-3.5 text-sm border-b border-slate-100 min-h-[44px] ${
                  selectedCategory === name ? 'bg-slate-50 font-semibold text-[#0F2C3E]' : 'text-slate-700 hover:bg-slate-50'
                }`}
                data-testid={`category-sheet-${categoryToSlug(name)}`}
              >
                <span>{name}</span>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-slate-400">{count}</span>
                  {selectedCategory === name && <Check className="h-4 w-4 text-[#0F2C3E]" />}
                </div>
              </Link>
            ))}
          </div>
        </SheetContent>
      </Sheet>

      {/* ═══════════════════════════════════════════════════════════════
          MOBILE FILTER SHEET  (display options only)
          ═══════════════════════════════════════════════════════════════ */}
      <Sheet open={filterDrawerOpen} onOpenChange={setFilterDrawerOpen}>
        <SheetContent side="bottom" className="max-h-[85vh] rounded-t-2xl" data-testid="mobile-filter-drawer">
          <SheetHeader className="text-left pb-2">
            <SheetTitle className="text-lg font-bold text-slate-900">Display</SheetTitle>
            <SheetDescription className="text-sm text-slate-500">Customise how listings appear</SheetDescription>
          </SheetHeader>
          <div className="px-0 pb-4 space-y-4 overflow-y-auto">
            <div>
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">View mode</label>
              <button
                type="button"
                onClick={() => setHideImages(!hideImages)}
                className={`flex items-center gap-2.5 w-full px-4 py-3 rounded-xl border text-sm font-medium min-h-[44px] transition-all ${
                  hideImages ? 'bg-slate-800 text-white border-slate-800' : 'bg-white text-slate-600 border-slate-200'
                }`}
                data-testid="drawer-hide-images-toggle"
              >
                <ImageOff className="h-4 w-4" />
                {hideImages ? 'Compact view on' : 'Compact view (hide images)'}
                {hideImages && <CheckCircle2 className="h-4 w-4 ml-auto" />}
              </button>
            </div>
          </div>
          <SheetFooter className="border-t border-slate-100 pt-3 flex-row gap-3 sm:flex-row">
            {hasActiveFilters && (
              <Button variant="outline" className="flex-1 h-11" onClick={() => { clearFilters(); setFilterDrawerOpen(false); }} data-testid="drawer-clear-filters">
                Clear all
              </Button>
            )}
            <SheetClose asChild>
              <Button className="flex-1 h-11 bg-[#0F2C3E] hover:bg-[#173f59] text-white" data-testid="drawer-apply-filters">
                Done
              </Button>
            </SheetClose>
          </SheetFooter>
        </SheetContent>
      </Sheet>

      <SignUpPromptModal isOpen={showSignUpModal} onClose={() => setShowSignUpModal(false)} variant="watchlist" />
    </div>
  );
};

export default BusinessesPage;
